import {Component, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ParksService} from '@app/app/shared/services/parks.service';
import {HttpClient} from '@angular/common/http';
import {environment} from '@app/environments/environment';
import {ParkInterface} from '@app/app/shared/interfaces/park.interface';
import {MessageService} from '@app/app/shared/services/message.service';
import {ThingScheduleInterface} from '@app/app/shared/interfaces/thing/thing-schedule.interface';
import * as moment from 'moment-timezone';
import {Subscription} from 'rxjs/Subscription';
import {AWSService} from '@app/app/shared/services/aws.service';
import {CommandsService} from '@app/app/shared/services/commands.service';
import {Observable} from 'rxjs/Observable';
import {TimerObservable} from 'rxjs/observable/TimerObservable';
import * as uuid from 'uuid';

interface ISchedule {
  begin: string;
  date?: string;
  days?: string;
  duration: number;
  end: string;
  event_id?: string;
  mode: string;
  seq_name: string;
}

@Component({
  templateUrl: './park-schedule-form.html',
  selector: 'park-schedule-form',
  encapsulation: ViewEncapsulation.None,
  styles: [`
    .gray-modal .modal-content {
      text-align: center;
      background-color: #EEE;
    }
  `]
})
export class ParkScheduleFormComponent implements OnInit, OnDestroy {
  constructor(private sender: MessageService, private modalService: NgbModal,
              private parksService: ParksService, private http: HttpClient,
              private aws: AWSService, private comm: CommandsService) {

    this.weeklyScheduleForm = new FormGroup({
      seq_name: new FormControl('', Validators.required),
      duration: new FormControl(1, [Validators.required, Validators.min(1), Validators.max(1440)]),
      begin: new FormControl('', Validators.required),
      end: new FormControl('', Validators.required),
      days: new FormGroup({
        1: new FormControl(),
        2: new FormControl(),
        3: new FormControl(),
        4: new FormControl(),
        5: new FormControl(),
        6: new FormControl(),
        7: new FormControl(),
      }, forbiddenNameValidator()),
      mode: new FormControl('', Validators.required),
    });

    this.yearlyScheduleForm = new FormGroup({
      seq_name: new FormControl('', Validators.required),
      duration: new FormControl(1, [Validators.required, Validators.min(1), Validators.max(1440)]),
      begin: new FormControl('', Validators.required),
      end: new FormControl('', Validators.required),
      date: new FormControl('', Validators.required),
      mode: new FormControl('', Validators.required),
    });
  }

  @Input() public park: ParkInterface;
  @Input() public formMode;
  @Input() public schedule: ThingScheduleInterface;
  @Input() public scheduleId: any;

  public weeklyScheduleForm: FormGroup;
  public yearlyScheduleForm: FormGroup;
  public isUploading = false;
  private desFile;
  public schCurYear = new Date().getFullYear();
  public openWeekly = true;
  public inProgress = false;
  private modalReference: any;
  messagesSubscription: Subscription;
  mqttConnection: any;
  currentTopic: string;
  private timerSub: Subscription;
  private timeoutSub: Subscription;

  open(content) {
    this.modalReference = this.modalService.open(content, {windowClass: 'gray-modal'});
  }

  public sequences() {
    if (typeof this.park.attributes.shadow !== 'undefined'
      && typeof this.park.attributes.shadow.schedule !== 'undefined'
      && typeof this.park.attributes.shadow.schedule.sequenceList !== 'undefined'
    ) {
      if (Array.isArray(this.park.attributes.shadow.schedule.sequenceList)) {
        return this.park.attributes.shadow.schedule.sequenceList;
      } else {
        const s = [];

        Object.keys(this.park.attributes.shadow.schedule.sequenceList).forEach(key => {
          s.push(this.park.attributes.shadow.schedule.sequenceList[key]);
        });

        return s;
      }
    }

    return [];
  }

  private prepareTime(hours, minutes = null) {
    if (minutes !== null) {
      return moment(`${hours}:${minutes}`, 'HH:mm').format('HH:mm');
    } else {
      return moment(hours, 'HH:mm').format('HH:mm');
    }
  }

  private prepareDate(months, days) {
    return moment(`${months}-${days}`, 'MM-DD').format('MM-DD');
  }

  public onSubmit({value, valid}) {
    if (valid) {
      this.inProgress = true;

      const params: ISchedule = {
        seq_name: value.seq_name,
        duration: Number(value.duration),
        begin: this.prepareTime(value.begin.hour, value.begin.minute),
        end: this.prepareTime(value.end.hour, value.end.minute),
        mode: value.mode
      };

      if (value.hasOwnProperty('days')) {
        params.days = Object.keys(value.days).filter(day => value.days[day] === true).join('');
      } else if (value.hasOwnProperty('date')) {
        params.date = this.prepareDate(value.date.month, value.date.day);
      }

      if (this.scheduleId) {
        params.event_id = this.scheduleId;

        this.comm.editSchedule(this.park.attributes.parkId, this.park.id, params).then((res) => {
          this.inProgress = false;

          if (this.modalReference) {
            this.modalReference.close();
          }
        }).catch((err) => {
          this.inProgress = false;

          if (err === 'overlap') {
            alert('The schedule overlaps with the existing one');
          }
        });
      } else {
        params.event_id = uuid.v4();

        this.comm.createSchedule(this.park.attributes.parkId, this.park.id, params).then((res) => {
          this.inProgress = false;

          this.weeklyScheduleForm.reset();
          this.yearlyScheduleForm.reset();

          if (this.modalReference) {
            this.modalReference.close();
          }
        }).catch((err) => {
          this.inProgress = false;

          if (err === 'overlap') {
            alert('The schedule overlaps with the existing one');
          }
          console.log(err);
        });
      }
    }
  }

  public deleteSchedule() {
    const params = {
      event_id: this.scheduleId
    };

    this.comm.deleteSchedule(this.park.attributes.parkId, this.park.id, params).then((res) => {
      this.inProgress = false;

      if (this.modalReference) {
        this.modalReference.close();
      }
    }).catch((err) => {
      this.inProgress = false;
    });
  }

  ngOnDestroy() {
    if (this.messagesSubscription) {
      this.messagesSubscription.unsubscribe();
    }

    if (this.timerSub) {
      this.timerSub.unsubscribe();
    }

    if (this.timeoutSub) {
      this.timeoutSub.unsubscribe();
    }
  }

  onFileChange(event) {
    const reader = new FileReader();

    if (event.target.files && event.target.files.length > 0) {
      this.isUploading = true;

      const file = event.target.files[0];
      reader.readAsDataURL(file);
      reader.onload = () => {
        const url = environment.apiUrl + '/sequences';
        const body = {
          seq_file: {
            filename: file.name,
            filetype: file.type,
            value: reader.result.split(',')[1]
          }
        };

        this.http.post(url, body).subscribe((res: { url: string, name: string }) => {
          if (res.hasOwnProperty('url') && res.hasOwnProperty('name')) {
            this.desFile = res.name;

            this.comm.updateSequences(this.park.attributes.parkId, this.park.id, res.url).then((res2) => {
              this.complete();
            }).catch((err2) => {
              this.complete();
            });
          }
        }, (err) => {
          console.log(err);
        });
      };
    }
  }

  private complete() {
    this.isUploading = false;

    const s = this.sequences();

    if (s.length < 1) {
      this.timerSub = TimerObservable.create(0, 250).subscribe(() => {
        const s2 = this.sequences();

        if (s2.length > 0) {
          this.setSequence(s2);

          if (this.timerSub) {
            this.timerSub.unsubscribe();
          }
        }
      });

      this.timeoutSub = Observable.of(true).delay(10000).subscribe(() => {
        if (this.timerSub) {
          this.timerSub.unsubscribe();
        }
      });
    } else {
      this.setSequence(s);
    }
  }

  private setSequence(s) {
    this.weeklyScheduleForm.patchValue({
      seq_name: s[0] || '',
    });

    this.yearlyScheduleForm.patchValue({
      seq_name: s[0] || '',
    });
  }

  ngOnInit() {
    if (this.schedule && this.schedule.hasOwnProperty('begin')) {
      const b = this.prepareTime(this.schedule.begin).split(':');
      const e = this.prepareTime(this.schedule.end).split(':');

      let begin = {hour: '00', minute: '00'};
      let end = {hour: '00', minute: '00'};

      if (b.length === 2) {
        begin = {hour: b[0], minute: b[1]};
      }

      if (e.length === 2) {
        end = {hour: e[0], minute: e[1]};
      }

      const seqName = this.schedule.seqName;
      const duration = parseInt(this.schedule.duration, 10);
      const mode = this.schedule.mode;
      const days = {1: '', 2: '', 3: '', 4: '', 5: '', 6: '', 7: ''};
      const date = {month: null, day: null, year: null};

      if (this.schedule.hasOwnProperty('days') && this.schedule.days) {
        this.openWeekly = true;
        const d = this.schedule.days.split('');

        if (d.length > 0 && d.length < 8) {
          d.forEach(day => {
            const dd = parseInt(day, 10);

            if (days.hasOwnProperty(dd)) {
              days[dd] = true;
            }
          });
        }
      } else if (this.schedule.hasOwnProperty('date') && this.schedule.date) {
        this.openWeekly = false;
        const d = this.schedule.date.split('-');

        if (d.length === 2) {
          date.month = parseInt(d[0], 10);
          date.day = parseInt(d[1], 10);
          date.year = this.schCurYear;
        }
      }

      this.weeklyScheduleForm.setValue({
        seq_name: seqName || '',
        duration: duration,
        begin: begin,
        end: end,
        mode: mode,
        days: days
      });

      this.yearlyScheduleForm.setValue({
        seq_name: seqName || '',
        duration: duration,
        begin: begin,
        end: end,
        mode: mode,
        date: date
      });
    } else {
      const today = new Date();

      const s = this.sequences();

      this.weeklyScheduleForm.setValue({
        seq_name: s[0] || '',
        duration: '1',
        begin: '',
        end: '',
        mode: 'activator',
        days: {1: '', 2: '', 3: '', 4: '', 5: '', 6: '', 7: ''}
      });

      this.yearlyScheduleForm.setValue({
        seq_name: s[0] || '',
        duration: '1',
        begin: '',
        end: '',
        mode: 'activator',
        date: {year: today.getFullYear() - 1, month: today.getMonth(), day: today.getDate()}
      });
    }
  }

  private scheduleTimeFormat() {
    const isAmPm = true;

    if (isAmPm) {
      return 'hh:mmA';
    } else {
      return 'HH:mm';
    }
  }

  private getParkTz(park) {
    return ((park.attributes || {}).location || {}).timezone || 'UTC';
  }

  formatTime(timeString: string) {
    return moment.tz(timeString, 'HH:mm', this.getParkTz(this.park)).format(this.scheduleTimeFormat());
  }

  formatTz() {
    return moment.tz.zone(this.getParkTz(this.park)).abbr(new Date().getTime());
  }
}

export function forbiddenNameValidator(): ValidatorFn {
  return (control: FormGroup): { [key: string]: any } => {
    let isValid = false;

    Object.keys(control.controls).forEach((key) => {
      if (control.controls[key].value === true) {
        isValid = true;
      }
    });

    return !isValid ? {'days': {value: false}} : null;
  };
}



// WEBPACK FOOTER //
// ./src/app/dashboard/forms/park-schedule-form/park-schedule-form.component.ts